import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { forkJoin, Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';
import { Context } from '../controls/advanced-gridview/models/context';
import { FilterInfos } from '../controls/advanced-gridview/models/filter-infos';
import { Results } from '../controls/advanced-gridview/models/results-T';
import { SortInfos } from '../controls/advanced-gridview/models/sort-infos';
import { AdvancedService } from '../controls/advanced-gridview/services/advanced-service';
import { BatchModification } from '../controls/inline-editable-table/models/batch-modifications';
import { BatchResult } from '../controls/inline-editable-table/models/batch-result';
import { BatchEditableTableServiceInterface } from '../controls/inline-editable-table/services/batch-editable-table-service-interface';
import { ArticleAlias } from '../models/article-alias';
import { CrudService } from './crud-service';
import { MemoryService } from './memory.service';

@Injectable({
    providedIn: 'root',
})
export class ArticleAliasesService extends CrudService implements AdvancedService<ArticleAlias>, BatchEditableTableServiceInterface<ArticleAlias> {
    constructor(
        private http: HttpClient,
        public override memoryService: MemoryService
    ) {
        super(memoryService);
    }

    List(context: Context, filters: FilterInfos[], generalFilters: FilterInfos[], sortInfos: SortInfos, pageNumber = 0, pageSize = 100): Observable<Results<ArticleAlias>> {
        let params = new Map<string, string | number | boolean>();
        params.set('from', pageNumber);
        params.set('size', pageSize);

        const httpOptions = this.getListHttpOptions(context, filters, generalFilters, sortInfos, params);
        if (context.property == 'articleId') {
            return this.http.get(this.baseUrl + `/api/organizations/${this.organizationId}/articles/${context.value}/articlealiases`, httpOptions).pipe(
                map((x) => {
                    var res = Results.getCopyFrom<ArticleAlias>(ArticleAlias, x);
                    return res;
                })
            );
        } else if (context.property == 'customerId') {
            return this.http.get(this.baseUrl + `/api/organizations/${this.organizationId}/customers/${context.value}/articlealiases`, httpOptions).pipe(
                map((x) => {
                    var res = Results.getCopyFrom<ArticleAlias>(ArticleAlias, x);
                    return res;
                })
            );
        } else {
            return of(null);
        }
    }

    Export(context: Context, filters: FilterInfos[], generalFilters: FilterInfos[], sortInfos: SortInfos): Observable<Blob> {
        const headers = this.getExportHeader(context, filters, generalFilters, sortInfos);

        var url = '';

        switch (context.property) {
            case 'customerId':
                url = this.baseUrl + `/api/organizations/${this.organizationId}/customers/${context.value}/ArticleAliases/export`;
                break;
            default:
                url = this.baseUrl + `/api/organizations/${this.organizationId}/articles/${context.value}/ArticleAliases/export`;
                break;
        }

        // Appel de la Web Api
        return this.http.get(url, { headers: headers, responseType: 'blob' }).pipe(
            map((res: any) => {
                const blob = new Blob([res], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
                return blob;
            })
        );
    }

    update(articleId: number, articleAliasId: number, articleAlias: ArticleAlias): Observable<ArticleAlias> {
        // Appel de la Web Api
        return this.http
            .put<ArticleAlias>(
                this.baseUrl + `/api/organizations/${this.organizationId}/Articles/${articleId}/ArticleAliases/${articleAliasId}`,
                articleAlias,
                this.getDefaultHttpOptions()
            )
            .pipe(
                map((x) => {
                    return x;
                })
            );
    }

    delete(articleId: number, articleAliasId: number): Observable<boolean> {
        console.log(`suppression de l'article ${articleId}`);
        // Appel de la Web Api
        return this.http
            .delete<ArticleAlias>(this.baseUrl + `/api/organizations/${this.organizationId}/Articles/${articleId}/ArticleAliases/${articleAliasId}`, this.getDefaultHttpOptions())
            .pipe(
                map((x) => {
                    return true;
                })
            );
    }

    add(articleId: number, articleAlias: ArticleAlias): Observable<ArticleAlias> {
        // Appel de la Web Api
        return this.http
            .post<ArticleAlias>(this.baseUrl + `/api/organizations/${this.organizationId}/Articles/${articleId}/ArticleAliases`, articleAlias, this.getDefaultHttpOptions())
            .pipe(
                map((x) => {
                    return x;
                })
            );
    }

    get(articleId: number, articleAliasId: number): Observable<ArticleAlias> {
        // Appel de la Web Api
        return this.http
            .get<ArticleAlias>(this.baseUrl + `/api/organizations/${this.organizationId}/Articles/${articleId}/ArticleAliases/${articleAliasId}`, this.getDefaultHttpOptions())
            .pipe(
                map((x) => {
                    return x;
                })
            );
    }

    public applyBatchModifications(context: Context, modifications: BatchModification<ArticleAlias>): Observable<BatchResult[]> {
        let requests = new Array<Observable<BatchResult>>();

        modifications.deletes.forEach((x) => {
            requests.push(this.delete(x.data.articleId, x.data.id as number).pipe(map((y) => new BatchResult(x.data.id, true))));
        });

        modifications.updates.forEach((x) => {
            requests.push(this.update(x.data.articleId, x.data.id, x.data).pipe(map((y) => new BatchResult(x.data.editableId, true))));
        });

        modifications.creates.forEach((x) => {
            if (context.property == 'articleId') {
                x.data.articleId = context.value as number;
            } else if (context.property == 'customerId') {
                x.data.customerId = context.value as number;
            }
            requests.push(this.add(x.data.articleId, x.data).pipe(map((y) => new BatchResult(x.data.id, true))));
        });

        return forkJoin(requests);
    }
}
